import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import * as pm from "@/api/provider-messages";
import { ThreadOutcomePanel } from "./ThreadOutcomePanel";

// ═══════════════════════════════════════════════════════════════════
// Provider messages — the one place a member talks to a BUSINESS.
//
// Everything else on WeDeshi is people talking to people. This is a thread
// rather than a wave because the business is answering in a work capacity and
// needs a record of it — and because it is the lead pipeline: the shop desk's
// "someone is asking" block is built from exactly these rows.
//
// Two behaviours worth seeing here:
//
//   · Marking read is a SEPARATE call from fetching. Opening a thread to skim
//     it and deciding you have dealt with it are different acts, and a read
//     receipt that fires on render makes the unread count useless as a worklist.
//   · The unread count is its own endpoint, not derived from the list, so a
//     badge does not require pulling every thread.
// ═══════════════════════════════════════════════════════════════════

export default function ProviderMessagesLab() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [status, setStatus] = useState<"" | pm.ProviderThreadStatus>("");

  const say = (m: string) => setLog((l) => [m, ...l].slice(0, 8));

  const threads = useQuery({
    queryKey: ["pm-threads", status],
    queryFn: () => pm.listProviderThreads(status ? { status } : undefined),
  });

  const unread = useQuery({
    queryKey: ["pm-unread"],
    queryFn: pm.getProviderUnreadCount,
  });

  const thread = useQuery({
    queryKey: ["pm-thread", openId],
    queryFn: () => pm.getProviderThread(openId as string),
    enabled: !!openId,
  });

  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    try {
      say(await fn());
      await qc.invalidateQueries({ queryKey: ["pm-threads"] });
      await qc.invalidateQueries({ queryKey: ["pm-unread"] });
      await qc.invalidateQueries({ queryKey: ["pm-thread"] });
    } catch (e) {
      say("✘ " + (e instanceof Error ? e.message : "failed"));
    } finally {
      setBusy(false);
    }
  };

  const list = threads.data?.data.threads ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Provider messages"
        subtitle="A member asking a business something — and the lead the business sees."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Providers", href: "/lab/providers" },
          { label: "Messages" },
        ]}
      />

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center gap-3 flex-wrap mb-3">
          <h2 className="text-sm font-medium text-gray-900">Threads</h2>
          {unread.data && (
            <Badge variant={unread.data.data.unread > 0 ? "green" : "gray"}>
              {unread.data.data.unread} unread
            </Badge>
          )}
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
            className="border border-gray-300 rounded-lg px-2 py-1 text-xs ml-auto"
          >
            <option value="">all</option>
            <option value="open">open</option>
            <option value="closed">closed</option>
          </select>
        </div>

        {threads.isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : threads.error ? (
          <p className="text-sm text-red-700">
            {threads.error instanceof Error ? threads.error.message : "Could not load"}
          </p>
        ) : !list.length ? (
          <p className="text-sm text-gray-500">
            No threads. A message starts when a member asks a business something
            from its listing.
          </p>
        ) : (
          <ul className="space-y-2">
            {list.map((t) => (
              <li
                key={t.threadId}
                className={`border rounded-xl px-4 py-3 text-sm ${
                  t.isUnread ? "border-gray-900 bg-gray-50" : "border-gray-200"
                }`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900">{t.from.name}</span>
                  {t.from.city && <span className="text-xs text-gray-500">{t.from.city}</span>}
                  <span className="text-xs text-gray-400">→ {t.provider.name}</span>
                  {t.isUnread && <Badge variant="green">unread</Badge>}
                  <Badge variant={t.status === "open" ? "blue" : "gray"}>{t.status}</Badge>
                  <button
                    onClick={() => setOpenId(openId === t.threadId ? null : t.threadId)}
                    className="ml-auto text-xs text-blue-600 hover:underline"
                  >
                    {openId === t.threadId ? "close" : "open"}
                  </button>
                </div>
                <p className="text-xs text-gray-600 mt-1">{t.preview}</p>

                {openId === t.threadId && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                    {thread.isLoading ? (
                      <p className="text-xs text-gray-500">Loading messages…</p>
                    ) : (
                      <ul className="space-y-2">
                        {(thread.data?.data.messages ?? []).map((m) => (
                          <li
                            key={m.id}
                            className={`text-xs rounded-lg px-3 py-2 ${
                              m.isFromProvider
                                ? "bg-blue-50 border border-blue-100"
                                : "bg-gray-50 border border-gray-100"
                            }`}
                          >
                            <span className="font-medium">{m.sender.name}</span>
                            {m.isFromProvider && <Badge variant="blue">business</Badge>}
                            <div className="text-gray-700 mt-0.5">{m.body}</div>
                            <button
                              className="text-red-600 hover:underline mt-1"
                              onClick={() =>
                                run(async () => {
                                  await pm.reportProviderMessage(m.id, "lab test report");
                                  return "reported";
                                })
                              }
                            >
                              report
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    <ThreadOutcomePanel threadId={t.threadId} />
                    <div className="flex gap-2 flex-wrap items-center">
                      <input
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        placeholder="Reply…"
                        className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-40"
                      />
                      <Button
                        size="sm"
                        isLoading={busy}
                        disabled={!reply.trim()}
                        onClick={() =>
                          run(async () => {
                            await pm.replyToProviderThread(t.threadId, reply.trim(), crypto.randomUUID());
                            setReply("");
                            return "replied";
                          })
                        }
                      >
                        Reply
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        isLoading={busy}
                        onClick={() =>
                          run(async () => {
                            await pm.markProviderThreadRead(t.threadId);
                            return "marked read — deliberately not automatic on open";
                          })
                        }
                      >
                        Mark read
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        isLoading={busy}
                        onClick={() =>
                          run(async () => {
                            await pm.closeProviderThread(t.threadId);
                            return "closed";
                          })
                        }
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {log.length > 0 && (
        <section className="bg-white border border-gray-200 rounded-2xl p-5">
          <h2 className="text-sm font-medium text-gray-900 mb-2">What happened</h2>
          <ul className="text-xs text-gray-600 space-y-1 font-mono">
            {log.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
